import type { AxiosResponse } from "axios";
import type { HttpClient } from "@/services/baseClient";
import type {
  ApiResponse,
  Track,
  CreateTrackRequest,
  TrackLayoutData,
  LeaderboardEntry,
} from "@/types";

export function createTracksApi({ client }: HttpClient) {
  return {
    async getTracks(): Promise<Track[]> {
      const response: AxiosResponse<{
        success: boolean;
        data: Track[];
        total: number;
      }> = await client.get("/tracks");
      return response.data.data;
    },

    async getTrack(id: string): Promise<Track> {
      const response: AxiosResponse<ApiResponse<Track>> =
        await client.get(`/tracks/${id}`);
      return response.data.data;
    },

    async createTrack(trackData: CreateTrackRequest): Promise<Track> {
      const response: AxiosResponse<ApiResponse<Track>> =
        await client.post("/tracks", trackData);
      return response.data.data;
    },

    async updateTrack(
      id: string,
      trackData: Partial<CreateTrackRequest>,
    ): Promise<Track> {
      const response: AxiosResponse<ApiResponse<Track>> =
        await client.put(`/tracks/${id}`, trackData);
      return response.data.data;
    },

    async deleteTrack(id: string): Promise<void> {
      await client.delete(`/tracks/${id}`);
    },

    async getUserVisitedTracksCount(): Promise<number> {
      const response: AxiosResponse<{
        success: boolean;
        data: { tracksVisited: number };
      }> = await client.get("/tracks/stats/visited");
      return response.data.data.tracksVisited;
    },

    async getTrackLeaderboard(id: string): Promise<LeaderboardEntry[]> {
      const response: AxiosResponse<ApiResponse<LeaderboardEntry[]>> =
        await client.get(`/tracks/${id}/leaderboard`);
      return response.data.data;
    },

    async getTrackLayout(id: string): Promise<TrackLayoutData> {
      const response: AxiosResponse<ApiResponse<TrackLayoutData>> =
        await client.get(`/tracks/${id}/layout`);
      return response.data.data;
    },

    async updateTrackLayout(
      id: string,
      layoutData: {
        layoutFileUrl: string;
        layoutFileHash?: string;
        layoutFileSize?: number;
      },
    ): Promise<Track> {
      const response: AxiosResponse<ApiResponse<Track>> =
        await client.put(`/tracks/${id}/layout`, layoutData);
      return response.data.data;
    },

    async removeTrackLayout(id: string): Promise<Track> {
      const response: AxiosResponse<ApiResponse<Track>> =
        await client.delete(`/tracks/${id}/layout`);
      return response.data.data;
    },
  };
}
